import React, { Fragment, useEffect, useState, useMemo } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useGetSeatById, useUpdateSeatMaintenance, useUpdateSeatRates } from "../../custom-hooks/use-seats";
import moment from "moment";
import { Breadcrumbs, Button, Chip, IconButton, Spinner, Progress, Tooltip } from "@material-tailwind/react";
import { convertToPresentableText, Notify } from "../../utils/functions";
import {
    CopyIcon, EyeIcon, HomeIcon,
    WifiIcon, MonitorIcon, MousePointerIcon, KeyboardIcon,
    ArmchairIcon, PowerIcon, SparklesIcon, WrenchIcon,
    CalendarIcon, DollarSignIcon, MapPinIcon, WindIcon,
    VolumeIcon, ThermometerIcon, SunIcon, ShieldCheckIcon,
    AccessibilityIcon, TagIcon, CheckCircleIcon,
    PencilIcon, SaveIcon, XIcon, UsersIcon
} from "lucide-react";
import { useConfirm } from "../../custom-hooks/confirm-dialog";
import { BottomUXSpace } from "../../components";
import BookingFormModal from "../booking/bookingFormModal";
import { InterpretAvailabilityStatus, InterpretOperationalStatus } from "../../utils/ui-interpreters";
import { URLS } from "../../router-x/routes";

const INITIAL_BOOKING_FORM_DATA = {
    seat: '',
    startDate: '',
    endDate: '',
    dailyStartTime: '',
    dailyEndTime: '',
};

// Functional component
const SeatDetailsPage = () => {
    const { seatId } = useParams();
    const navigate = useNavigate();
    const [isBookingFormModalOpen, setIsBookingFormModalOpen] = useState(false);
    const [bookingDataForModal, setBookingDataForModal] = useState(INITIAL_BOOKING_FORM_DATA);
    const [isEditingRates, setIsEditingRates] = useState(false);
    const { confirm, ConfirmationDialog } = useConfirm();

    const {
        data: seatDetails,
        isLoading: isSeatDetailsLoading,
        error: seatDetailsError,
        refetch: refetchSeatDetails,
    } = useGetSeatById(seatId);

    const seat = useMemo(() => seatDetails?.data?.seat || {}, [seatDetails?.data?.seat]);

    const [rates, setRates] = useState({
        ratePerHour: seat?.ratePerHour || 0,
        ratePerDay: seat?.ratePerDay || 0,
        ratePerMonth: seat?.ratePerMonth || 0,
    });


    // const updateSeatStatusMutation = useUpdateSeatStatus();
    const updateSeatMaintenanceMutation = useUpdateSeatMaintenance();
    const updateSeatRatesMutation = useUpdateSeatRates();

    useEffect(() => {
        if (seat) {
            setRates({
                ratePerHour: seat?.ratePerHour || 0,
                ratePerDay: seat?.ratePerDay || 0,
                ratePerMonth: seat?.ratePerMonth || 0,
            });
        }
    }, [seat]);

    if (isSeatDetailsLoading) {
        return (
            <div className={`flex justify-center p-4 m-4 items-center h-screen border rounded-3xl`}>
                <Spinner />
            </div>
        );
    }

    if (seatDetailsError) {
        return (
            <Fragment>
                <div className="p-4 flex flex-col justify-center items-center h-96">
                    <div className="p-4 text-red-600 text-center">
                        <h2 className="text-xl font-bold">Error Loading Seat</h2>
                        <p>{seatDetailsError.message || "Failed to load seat details"}</p>
                        <div>
                            <span className="text-gray-500 text-xs">Seat Id: {seatId}</span>
                        </div>
                        <Button className="rounded-full mt-4" variant="gradient" color="gray">
                            <span className="text-sm" onClick={() => navigate(URLS.ADMIN.SEAT)}>
                                Go Back
                            </span>
                        </Button>
                    </div>
                </div>
            </Fragment>
        );
    }

    const copyToClipboard = (text) => {
        navigator.clipboard.writeText(text);
        Notify("Copied to clipboard", "success");
    }

    const handleToggleMaintenance = async () => {
        const confirmation = await confirm({
            title: seat.underMaintenance ? "Mark as Operational" : "Mark Under Maintenance",
            message: seat.underMaintenance
                ? "Are you sure you want to mark this seat as operational?"
                : "Are you sure you want to mark this seat under maintenance?",
            confirmText: "Confirm",
            cancelText: "Cancel",
            confirmBtnColor: seat.underMaintenance ? "green" : "orange",
        });

        if (confirmation) {
            await updateSeatMaintenanceMutation.mutateAsync({
                seatId: seatId,
                underMaintenance: !seat.underMaintenance,
            });
            refetchSeatDetails();
        }
    };

    const handleSaveRates = async () => {
        try {
            await updateSeatRatesMutation.mutateAsync({
                seatId: seatId,
                ratePerHour: rates.ratePerHour,
                ratePerDay: rates.ratePerDay,
                ratePerMonth: rates.ratePerMonth,
            });
            setIsEditingRates(false);
            refetchSeatDetails();
        } catch (error) {
            console.error("Error updating rates:", error);
        }
    };

    const handleCancelEditRates = () => {
        setRates({
            ratePerHour: seat?.ratePerHour || 0,
            ratePerDay: seat?.ratePerDay || 0,
            ratePerMonth: seat?.ratePerMonth || 0,
        });
        setIsEditingRates(false);
    };

    const handleAddBooking = () => {
        setBookingDataForModal({
            ...INITIAL_BOOKING_FORM_DATA,
            seat: seatId,
            startDate: moment().format('YYYY-MM-DD'),
        });
        setIsBookingFormModalOpen(true);
    };

    return (
        <div className="p-4 space-y-6">
            {/*
            * //* Seat Profile Card
            */}
            <div className="border rounded-3xl p-6">
                <div className="">
                    <Breadcrumbs className="mb-4">
                        <Link to={URLS.ADMIN.ROOT} className="opacity-60 flex items-center gap-1">
                            <HomeIcon size={14} />
                            <span>Home</span>
                        </Link>
                        <Link to={URLS.ADMIN.SEAT} className="opacity-60 flex items-center gap-1">
                            <ArmchairIcon size={14} />
                            <span>Seats</span>
                        </Link>
                        <span className="flex items-center gap-1">
                            {isSeatDetailsLoading ? (
                                <Spinner size="sm" />
                            ) : (
                                <>
                                    <ArmchairIcon size={14} />
                                    <span>{seat?.seatCode || 'Seat Details'}</span>
                                </>
                            )}
                        </span>
                    </Breadcrumbs>
                </div>

                <div className="flex items-start justify-between">
                    <div>
                        <h1 className="text-2xl font-bold text-gray-800">{seat.seatCode || "Seat Code"}</h1>
                        <p className="text-sm text-gray-500 mt-1">
                            Shared Space: {seat.coworking?.code || "Coworking Space"} • {convertToPresentableText(seat.seatType)} Seat
                        </p>
                    </div>
                    <div className="flex items-center space-x-2">
                        {InterpretAvailabilityStatus(seat.availabilityStatus)}
                        {InterpretOperationalStatus(seat.status)}
                        {seat.underMaintenance && (
                            <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                                Under Maintenance
                            </span>
                        )}
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-6">
                    {/* Location Information */}
                    <div>
                        <h2 className="text-lg font-semibold mb-3 text-gray-700 flex items-center gap-2">
                            <MapPinIcon size={18} />
                            Location
                        </h2>
                        <div className="space-y-2 text-sm">
                            <div>
                                <span className="text-gray-500">Zone:</span>
                                <span className="text-gray-800 font-medium ml-2">
                                    {seat.position?.zone ? convertToPresentableText(seat.position.zone) : "N/A"}
                                </span>
                            </div>
                            <div>
                                <span className="text-gray-500">Placement:</span>
                                <span className="text-gray-800 font-medium ml-2">
                                    {seat.position?.placement ? convertToPresentableText(seat.position.placement) : "N/A"}
                                </span>
                            </div>
                            <div>
                                <span className="text-gray-500">Desk Type:</span>
                                <span className="text-gray-800 font-medium ml-2">
                                    {convertToPresentableText(seat.deskType)}
                                </span>
                            </div>
                        </div>
                    </div>

                    {/* Utilization Stats */}
                    <div>
                        <h2 className="text-lg font-semibold mb-3 text-gray-700 flex items-center gap-2">
                            <UsersIcon size={18} />
                            Utilization
                        </h2>
                        <div className="space-y-3">
                            <div>
                                <div className="flex justify-between text-sm mb-1">
                                    <span className="text-gray-500">Average Utilization</span>
                                    <span className="font-medium">{seat.averageUtilization || 0}%</span>
                                </div>
                                <Progress value={seat.averageUtilization || 0} color="blue" />
                            </div>
                            <div className="text-sm">
                                <span className="text-gray-500">Available Hours:</span>
                                <span className="text-gray-800 font-medium ml-2">{seat.availableHours || 24}h</span>
                            </div>
                            <div className="text-sm">
                                <span className="text-gray-500">Occupied Hours:</span>
                                <span className="text-gray-800 font-medium ml-2">{seat.occupiedHours || 0}h</span>
                            </div>
                        </div>
                    </div>

                    {/* Quick Actions */}
                    <div>
                        <h2 className="text-lg font-semibold mb-3 text-gray-700">Quick Actions</h2>
                        <div className="space-y-2">
                            <Tooltip content="This functionality will be available soon">
                                <Button
                                    className="w-full rounded-full"
                                    variant="gradient"
                                    color="blue"
                                    size="sm"
                                    // onClick={handleAddBooking}
                                    disabled={!seat.isBookable || seat.underMaintenance}
                                >
                                    Book This Seat
                                </Button>
                            </Tooltip>
                            <Button
                                className="w-full rounded-full"
                                variant="gradient"
                                color={seat.underMaintenance ? "green" : "orange"}
                                size="sm"
                                onClick={handleToggleMaintenance}
                                loading={updateSeatMaintenanceMutation.isLoading}
                            >
                                {seat.underMaintenance ? "Mark as Operational" : "Mark for Maintenance"}
                            </Button>
                        </div>
                    </div>
                </div>
            </div>

            {/*
       * //* Features and Equipment
       */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="border rounded-3xl p-6">
                    <h2 className="text-lg font-semibold mb-4 text-gray-700 flex items-center gap-2">
                        <MonitorIcon size={18} />
                        Equipment & Features
                    </h2>
                    <div className="grid grid-cols-2 gap-3">
                        {[
                            { icon: <MonitorIcon size={16} />, label: "Monitor Arm", value: seat.hasMonitorArm },
                            { icon: <KeyboardIcon size={16} />, label: "Keyboard", value: seat.hasKeyboard },
                            { icon: <MousePointerIcon size={16} />, label: "Mouse", value: seat.hasMouse },
                            { icon: <ArmchairIcon size={16} />, label: "Ergonomic Chair", value: seat.hasErgonomicChair },
                            { icon: <PowerIcon size={16} />, label: "USB Ports", value: seat.hasUSBPorts },
                            { icon: <WifiIcon size={16} />, label: "Wired Internet", value: seat.hasWiredInternet },
                            { icon: <ShieldCheckIcon size={16} />, label: "Privacy Screen", value: seat.hasPrivacyScreen },
                            { icon: <AccessibilityIcon size={16} />, label: "Wheelchair Accessible", value: seat.isWheelchairAccessible },
                        ].map((feature, idx) => (
                            <div key={idx} className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg">
                                <span className={feature.value ? "text-green-600" : "text-gray-400"}>
                                    {feature.icon}
                                </span>
                                <span className={`text-sm ${feature.value ? "text-gray-800" : "text-gray-400"}`}>
                                    {feature.label}
                                </span>
                                {feature.value ? (
                                    <CheckCircleIcon size={14} className="ml-auto text-green-600" />
                                ) : (
                                    <XIcon size={14} className="ml-auto text-gray-400" />
                                )}
                            </div>
                        ))}
                    </div>
                    {seat.internetSpeed && (
                        <div className="mt-4 p-3 bg-blue-50 rounded-lg">
                            <div className="flex items-center gap-2">
                                <WifiIcon size={16} className="text-blue-600" />
                                <span className="text-sm font-medium">Internet Speed: {seat.internetSpeed} Mbps</span>
                            </div>
                        </div>
                    )}
                </div>

                {/*
         * //* Environmental Factors
         */}
                <div className="border rounded-3xl p-6">
                    <h2 className="text-lg font-semibold mb-4 text-gray-700 flex items-center gap-2">
                        <WindIcon size={18} />
                        Environmental Factors
                    </h2>
                    <div className="space-y-3">
                        <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                            <div className="flex items-center gap-2">
                                <SunIcon size={16} className="text-yellow-600" />
                                <span className="text-sm">Natural Light</span>
                            </div>
                            <Chip
                                color={seat.naturalLight ? "green" : "gray"}
                                size="sm"
                                variant="ghost"
                                value={seat.naturalLight ? "Yes" : "No"}
                                className="rounded-full"
                            />
                        </div>
                        <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                            <div className="flex items-center gap-2">
                                <VolumeIcon size={16} className="text-blue-600" />
                                <span className="text-sm">Noise Level</span>
                            </div>
                            <Chip
                                color={seat.noiseLevel === 'low' ? "green" : seat.noiseLevel === 'high' ? "red" : "yellow"}
                                size="sm"
                                variant="ghost"
                                value={convertToPresentableText(seat.noiseLevel || 'medium')}
                                className="rounded-full"
                            />
                        </div>
                        <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                            <div className="flex items-center gap-2">
                                <MapPinIcon size={16} className="text-green-600" />
                                <span className="text-sm">Near Window</span>
                            </div>
                            <Chip
                                color={seat.nearWindow ? "green" : "gray"}
                                size="sm"
                                variant="ghost"
                                value={seat.nearWindow ? "Yes" : "No"}
                                className="rounded-full"
                            />
                        </div>
                        {seat.temperature && (
                            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                                <div className="flex items-center gap-2">
                                    <ThermometerIcon size={16} className="text-orange-600" />
                                    <span className="text-sm">Average Temperature</span>
                                </div>
                                <span className="text-sm font-medium">{seat.temperature}°C</span>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/*
       * //* Pricing & Billing
       */}
            <div className="border rounded-3xl p-6">
                <div className="flex items-center justify-between mb-4">
                    <h2 className="text-lg font-semibold text-gray-700 flex items-center gap-2">
                        <DollarSignIcon size={20} />
                        Pricing & Billing
                    </h2>
                    {!isEditingRates ? (
                        <IconButton
                            variant="text"
                            size="sm"
                            onClick={() => setIsEditingRates(true)}
                            className="rounded-full"
                        >
                            <PencilIcon size={16} />
                        </IconButton>
                    ) : (
                        <div className="flex gap-2">
                            <IconButton
                                variant="gradient"
                                color="blue"
                                size="sm"
                                onClick={handleSaveRates}
                                className="rounded-full"
                                disabled={updateSeatRatesMutation.isLoading}
                            >
                                <SaveIcon size={16} />
                            </IconButton>
                            <IconButton
                                variant="outlined"
                                size="sm"
                                onClick={handleCancelEditRates}
                                className="rounded-full"
                                disabled={updateSeatRatesMutation.isLoading}
                            >
                                <XIcon size={16} />
                            </IconButton>
                        </div>
                    )}
                </div>

                {!isEditingRates ? (
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div className="bg-gray-200 rounded-lg p-4">
                            <p className="text-sm text-gray-600 mb-1">Hourly Rate</p>
                            <p className="text-2xl font-bold text-gray-800">
                                PKR {rates.ratePerHour || 0}
                            </p>
                        </div>
                        <div className="bg-gray-200 rounded-lg p-4">
                            <p className="text-sm text-gray-600 mb-1">Daily Rate</p>
                            <p className="text-2xl font-bold text-gray-800">
                                PKR {rates.ratePerDay || 0}
                            </p>
                        </div>
                        <div className="bg-gray-200 rounded-lg p-4">
                            <p className="text-sm text-gray-600 mb-1">Monthly Rate</p>
                            <p className="text-2xl font-bold text-gray-800">
                                PKR {rates.ratePerMonth || 0}
                            </p>
                        </div>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                            <label className="text-sm text-gray-600">Hourly Rate (PKR)</label>
                            <input
                                type="number"
                                className="mt-1 w-full px-3 py-2 border rounded-lg"
                                value={rates.ratePerHour}
                                onChange={(e) => setRates({ ...rates, ratePerHour: e.target.value })}
                                disabled={updateSeatRatesMutation.isLoading}
                            />
                        </div>
                        <div>
                            <label className="text-sm text-gray-600">Daily Rate (PKR)</label>
                            <input
                                type="number"
                                className="mt-1 w-full px-3 py-2 border rounded-lg"
                                value={rates.ratePerDay}
                                onChange={(e) => setRates({ ...rates, ratePerDay: e.target.value })}
                                disabled={updateSeatRatesMutation.isLoading}
                            />
                        </div>
                        <div>
                            <label className="text-sm text-gray-600">Monthly Rate (PKR)</label>
                            <input
                                type="number"
                                className="mt-1 w-full px-3 py-2 border rounded-lg"
                                value={rates.ratePerMonth}
                                onChange={(e) => setRates({ ...rates, ratePerMonth: e.target.value })}
                                disabled={updateSeatRatesMutation.isLoading}
                            />
                        </div>
                    </div>
                )}

                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4 pt-4 border-t">
                    <div>
                        <p className="text-sm text-gray-600">Cost Center</p>
                        <p className="font-medium">{seat.costCenter || "N/A"}</p>
                    </div>
                    <div>
                        <p className="text-sm text-gray-600">Department Code</p>
                        <p className="font-medium">{seat.departmentCode || "N/A"}</p>
                    </div>
                </div>
            </div>

            {/*
       * //* Maintenance & Cleaning
       */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="border rounded-3xl p-6">
                    <h2 className="text-lg font-semibold mb-4 text-gray-700 flex items-center gap-2">
                        <WrenchIcon size={18} />
                        Maintenance
                    </h2>
                    <div className="space-y-3">
                        <div className="flex items-center justify-between">
                            <span className="text-sm text-gray-600">Status</span>
                            <Chip
                                color={seat.needsMaintenance ? "red" : "green"}
                                size="sm"
                                variant="ghost"
                                value={seat.needsMaintenance ? "Needs Maintenance" : "Good Condition"}
                                className="rounded-full"
                            />
                        </div>
                        {seat.lastMaintenanceDate && (
                            <div className="flex items-center justify-between">
                                <span className="text-sm text-gray-600">Last Maintenance</span>
                                <span className="text-sm font-medium">
                                    {moment(seat.lastMaintenanceDate).format("MMM DD, YYYY")}
                                </span>
                            </div>
                        )}
                        {seat.nextMaintenanceDate && (
                            <div className="flex items-center justify-between">
                                <span className="text-sm text-gray-600">Next Scheduled</span>
                                <span className="text-sm font-medium">
                                    {moment(seat.nextMaintenanceDate).format("MMM DD, YYYY")}
                                </span>
                            </div>
                        )}
                        {seat.maintenanceNotes && (
                            <div className="mt-3 p-3 bg-gray-50 rounded-lg">
                                <p className="text-xs text-gray-600 mb-1">Notes</p>
                                <p className="text-sm">{seat.maintenanceNotes}</p>
                            </div>
                        )}
                    </div>
                </div>

                <div className="border rounded-3xl p-6">
                    <h2 className="text-lg font-semibold mb-4 text-gray-700 flex items-center gap-2">
                        <SparklesIcon size={18} />
                        Cleaning Schedule
                    </h2>
                    <div className="space-y-3">
                        <div className="flex items-center justify-between">
                            <span className="text-sm text-gray-600">Sanitization Status</span>
                            <Chip
                                color={seat.cleaning?.sanitizationStatus === 'clean' ? "green" :
                                    seat.cleaning?.sanitizationStatus === 'needs_cleaning' ? "red" : "yellow"}
                                size="sm"
                                variant="ghost"
                                value={convertToPresentableText(seat.cleaning?.sanitizationStatus || 'clean')}
                                className="rounded-full"
                            />
                        </div>
                        {seat.cleaning?.lastCleaned && (
                            <div className="flex items-center justify-between">
                                <span className="text-sm text-gray-600">Last Cleaned</span>
                                <span className="text-sm font-medium">
                                    {moment(seat.cleaning.lastCleaned).format("MMM DD, YYYY")}
                                </span>
                            </div>
                        )}
                        {seat.cleaning?.cleaningSchedule?.frequency && (
                            <div className="flex items-center justify-between">
                                <span className="text-sm text-gray-600">Frequency</span>
                                <span className="text-sm font-medium capitalize">
                                    {seat.cleaning.cleaningSchedule.frequency}
                                </span>
                            </div>
                        )}
                        {seat.cleaning?.cleaningSchedule?.nextScheduledDate && (
                            <div className="flex items-center justify-between">
                                <span className="text-sm text-gray-600">Next Scheduled</span>
                                <span className="text-sm font-medium">
                                    {moment(seat.cleaning.cleaningSchedule.nextScheduledDate).format("MMM DD, YYYY")}
                                </span>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/*
       * //* Booking Rules
       */}
            <div className="border rounded-3xl p-6">
                <h2 className="text-lg font-semibold mb-4 text-gray-700 flex items-center gap-2">
                    <CalendarIcon size={18} />
                    Booking Rules
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="p-4 bg-gray-50 rounded-lg">
                        <p className="text-sm text-gray-600 mb-1">Bookable</p>
                        <Chip
                            color={seat.isBookable ? "green" : "red"}
                            size="sm"
                            variant="ghost"
                            value={seat.isBookable ? "Yes" : "No"}
                            className="rounded-full"
                        />
                    </div>
                    <div className="p-4 bg-gray-50 rounded-lg">
                        <p className="text-sm text-gray-600 mb-1">Min Duration</p>
                        <p className="font-medium">{seat.minimumBookingDuration || 30} minutes</p>
                    </div>
                    <div className="p-4 bg-gray-50 rounded-lg">
                        <p className="text-sm text-gray-600 mb-1">Max Duration</p>
                        <p className="font-medium">{seat.maximumBookingDuration ? `${seat.maximumBookingDuration} minutes` : "No limit"}</p>
                    </div>
                    <div className="p-4 bg-gray-50 rounded-lg">
                        <p className="text-sm text-gray-600 mb-1">Advance Booking</p>
                        <Chip
                            color={seat.advanceBookingRequired ? "blue" : "gray"}
                            size="sm"
                            variant="ghost"
                            value={seat.advanceBookingRequired ? `${seat.advanceBookingTime || 0}h required` : "Not required"}
                            className="rounded-full"
                        />
                    </div>
                    <div className="p-4 bg-gray-50 rounded-lg">
                        <p className="text-sm text-gray-600 mb-1">Recurring Bookings</p>
                        <Chip
                            color={seat.allowRecurringBookings ? "green" : "red"}
                            size="sm"
                            variant="ghost"
                            value={seat.allowRecurringBookings ? "Allowed" : "Not Allowed"}
                            className="rounded-full"
                        />
                    </div>
                </div>

                {seat.restrictedHours && seat.restrictedHours.length > 0 && (
                    <div className="mt-4 p-4 bg-yellow-50 rounded-lg">
                        <p className="text-sm font-medium text-yellow-800 mb-2">Restricted Hours</p>
                        <div className="space-y-1">
                            {seat.restrictedHours.map((restriction, idx) => (
                                <div key={idx} className="text-xs text-yellow-700">
                                    {restriction.day}: {moment(restriction.startTime).format("h:mm A")} - {moment(restriction.endTime).format("h:mm A")}
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>

            {/*
       * //* Current Bookings
       */}
            <div className="border rounded-3xl p-6">
                <div className="flex justify-between items-center mb-4">
                    <h2 className="text-xl font-bold text-gray-800">Current & Upcoming Bookings</h2>
                    <Button
                        className="rounded-full hidden"
                        size="sm"
                        variant="gradient"
                        onClick={handleAddBooking}
                        disabled={!seat.isBookable || seat.underMaintenance}
                    >
                        <span className="text-sm">Create New Booking</span>
                    </Button>
                </div>

                {(!seat.bookings || seat.bookings.length === 0) ? (
                    <div className="text-center py-8 text-gray-500">
                        No bookings found for this seat
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">User</th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date & Time</th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Duration</th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {seat.bookings.map((booking) => (
                                    <tr key={booking._id}>
                                        <td className="px-4 py-3 whitespace-nowrap">
                                            <div className="text-sm font-medium text-gray-900">
                                                {booking.contract.user?.name || 'Unknown User'}
                                            </div>
                                            <div className="text-xs text-gray-500">
                                                {booking.contract.user?.email}
                                            </div>
                                        </td>
                                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                                            <div>{moment(booking.startDate).format("MMM DD, YYYY")} - {booking.endDate ? moment(booking.endDate).format("MMM DD, YYYY") : 'Open'}</div>
                                            {booking.dailyStartTime && (
                                                <div className="text-xs">
                                                    {moment(booking.dailyStartTime).format("h:mm A")} - {moment(booking.dailyEndTime).format("h:mm A")}
                                                </div>
                                            )}
                                        </td>
                                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                                            {booking.endDate ?
                                                moment(booking.endDate).diff(moment(booking.startDate), 'days') + ' days' :
                                                'Open (' + booking.contract.noticePeriod + ' weeks)'
                                            }
                                        </td>
                                        <td className="px-4 py-3 whitespace-nowrap">
                                            <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusBadgeColor(booking.status)}`}>
                                                {convertToPresentableText(booking.status)}
                                            </span>
                                        </td>
                                        <td className="px-4 py-3 whitespace-nowrap">
                                            <IconButton
                                                variant="gradient"
                                                color="blue"
                                                size="sm"
                                                className="rounded-full"
                                                onClick={() => navigate(`${URLS.ADMIN.BOOKING}/${booking._id}`)}
                                            >
                                                <EyeIcon size={16} />
                                            </IconButton>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/*
       * //* Additional Information
       */}
            <div className="border rounded-3xl p-6">
                <h2 className="text-lg font-semibold mb-4 text-gray-700 flex items-center gap-2">
                    <TagIcon size={18} />
                    Additional Information
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                        <h3 className="text-md font-medium mb-2 text-gray-600">Tags</h3>
                        <div className="flex flex-wrap gap-2">
                            {seat.tags && seat.tags.length > 0 ? (
                                seat.tags.map((tag, idx) => (
                                    <Chip
                                        key={idx}
                                        size="sm"
                                        variant="outlined"
                                        value={tag}
                                        className="rounded-full"
                                    />
                                ))
                            ) : (
                                <span className="text-sm text-gray-400">No tags</span>
                            )}
                        </div>
                    </div>
                    <div>
                        <h3 className="text-md font-medium mb-2 text-gray-600">System Information</h3>
                        <div className="space-y-2 text-xs">
                            <div className="flex items-start">
                                <span className="text-gray-500 w-24">Seat ID:</span>
                                <span className="text-gray-800 font-medium truncate flex items-center gap-1">
                                    <CopyIcon size={12} onClick={() => copyToClipboard(seat._id)} className="cursor-pointer" />
                                    {seat._id || "N/A"}
                                </span>
                            </div>
                            <div className="flex items-start">
                                <span className="text-gray-500 w-24">Created:</span>
                                <span className="text-gray-800 font-medium">
                                    {seat.createdAt ? moment(seat.createdAt).format("LLL") : "N/A"}
                                </span>
                            </div>
                            <div className="flex items-start">
                                <span className="text-gray-500 w-24">Updated:</span>
                                <span className="text-gray-800 font-medium">
                                    {seat.updatedAt ? moment(seat.updatedAt).format("LLL") : "N/A"}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
                {seat.notes && (
                    <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                        <h3 className="text-md font-medium mb-2 text-gray-600">Notes</h3>
                        <p className="text-sm text-gray-700">{seat.notes}</p>
                    </div>
                )}
            </div>

            <BottomUXSpace />

            {isBookingFormModalOpen && (
                <BookingFormModal
                    isOpen={isBookingFormModalOpen}
                    onClose={() => {
                        setIsBookingFormModalOpen(false);
                        setBookingDataForModal(INITIAL_BOOKING_FORM_DATA);
                    }}
                    defaultData={bookingDataForModal}
                    isUpdating={false}
                    onSuccess={() => {
                        // refetchBookings();
                        refetchSeatDetails();
                    }}
                />
            )}

            <ConfirmationDialog />
        </div>
    );
};

// Helper function for status badge colors
function getStatusBadgeColor(status) {
    switch (status) {
        case 'approved':
            return 'bg-green-100 text-green-800';
        case 'pending':
            return 'bg-yellow-100 text-yellow-800';
        case 'rejected':
            return 'bg-red-100 text-red-800';
        case 'cancelled':
            return 'bg-gray-100 text-gray-800';
        case 'completed':
            return 'bg-blue-100 text-blue-800';
        default:
            return 'bg-gray-100 text-gray-800';
    }
}

export default SeatDetailsPage;